import { useSearchParams } from 'react-router-dom';
import { Nav, NavItem } from 'reactstrap';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { HELP_TOPICS } from '../constants/helpTopics.jsx';

// Mirror of the AppLayout page transition (see appLayout.jsx) so a
// topic switch *feels* like a page change visually without actually
// being one (which would re-trigger the layout-level transition and
// nuke the sidebar too).
const panelVariants = {
    initial: { x: 40, opacity: 0 },
    animate: {
        x: 0,
        opacity: 1,
        transition: {
            duration: 0.2,
            ease: [0.25, 0.1, 0.25, 1],
            opacity: { delay: 0.06 },
        },
    },
    exit: {
        opacity: 0,
        transition: { duration: 0.1, ease: 'easeOut' },
    },
};

// In-app help. Sidebar of topics on the left, content panel on the right.
// Mirrors the pattern we'll use for the upcoming settings redesign so the
// two pages feel consistent.
//
// URL sync uses a query param (/help?topic=quotes) rather than a path
// segment so the AppLayout's pathname-keyed Framer Motion transition
// (`key={location.pathname}`) doesn't fire on every topic click — only
// the search string changes, the route stays mounted. Unknown / missing
// `topic` falls back to the first topic rather than 404'ing — a stale
// shared link should still land someone on the help page.
export default function HelpPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const topicParam = searchParams.get('topic');
    const prefersReducedMotion = useReducedMotion();

    const selected = HELP_TOPICS.find(t => t.id === topicParam) || HELP_TOPICS[0];

    const selectTopic = (id) => {
        setSearchParams({ topic: id }, { replace: false });
    };

    return (
        <div>
            <h1 className="fw-bold mb-4">Help</h1>
            <div className="row g-4">
                <aside className="col-12 col-md-4 col-lg-3">
                    <div className="surface p-2">
                        <Nav vertical pills className="help-nav">
                            {HELP_TOPICS.map(t => (
                                <NavItem key={t.id}>
                                    <button
                                        type="button"
                                        className={`nav-link text-start w-100${t.id === selected.id ? ' active' : ''}`}
                                        onClick={() => selectTopic(t.id)}
                                    >
                                        {t.label}
                                    </button>
                                </NavItem>
                            ))}
                        </Nav>
                    </div>
                </aside>
                <section className="col-12 col-md-8 col-lg-9">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={selected.id}
                            className="surface p-4"
                            variants={prefersReducedMotion ? undefined : panelVariants}
                            initial="initial"
                            animate="animate"
                            exit="exit"
                            transition={prefersReducedMotion ? { duration: 0 } : undefined}
                        >
                            <h2 className="fw-semibold fs-4 mb-3">{selected.label}</h2>
                            {selected.render()}
                        </motion.div>
                    </AnimatePresence>
                </section>
            </div>
        </div>
    );
}
